import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as FrequencyIcon } from '@/assets/icons/frequency.svg';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import FullScreenButton from '@/components/FullScreenButton';
import { PageHeading } from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostType } from '@/utils/enums';
import { set } from '@/utils/storage';

import ArchiveTable from './components/ArchiveTable';
import AutoDeleteNotification from './components/AutoDeleteNotification';
import FailedPostsNotification from './components/FailedPostsNotification';
import FrequencyModal from './components/FrequencyModal';
import NetworkSwitch from './components/NetworkSwitch';
import NewPostModal from './components/NewPostModal';
import PagesSelection from './components/PagesSelection';
import PerformanceHoldNotification from './components/PerformanceHoldNotification';
import PerformanceModal from './components/PerformanceModal';
import PostsTable from './components/PostsTable';
import QueueSwitch from './components/QueueSwitch';
import Table from './components/Table';
import ImageAlbumPostForm from './components/forms/ImageAlbumPostForm';
import ImagePostForm from './components/forms/ImagePostForm';
import LinkPostForm from './components/forms/LinkPostForm';
import PlaceholderPostForm from './components/forms/PlaceholderPostForm';
import StoryPostForm from './components/forms/StoryPostForm';
import TextPostForm from './components/forms/TextPostForm';
import VideoPostForm from './components/forms/VideoPostForm';

const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const NewPostButton = styled(Button)`
  padding: 0 32px;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 0 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Notifications = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledFullScreenButton = styled(FullScreenButton)`
  margin: 0;
`;

export const StyledFilterButton = styled(FilterButton)`
  margin: 0;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

const StyledNetworkSwitch = styled(NetworkSwitch)`
  border-color: ${({ theme }) => theme.colors.lightGray};
`;

const NotSupportedDescription = styled.div`
  border-radius: 4px;
  background: white;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  svg {
    width: 64px;
    color: ${({ theme }) => theme.colors.error};
  }
`;

/**
 * Steps for adding new network:
 * 1. Update EditorialDistributionQueue/index.tsx
 * 2. Update EditorialQueuePage interface definition
 * 3. Update EditorialFacebookPostNetwork enum
 * 4. Update dq.ts helper functions
 * 5. Update NewPostModal.tsx
 * 6. Update NetworkSwitch.tsx
 * 7. Update OverviewModal.tsx
 * 8. Update ExportModal.tsx
 * 9. Update PagesSelection.tsx
 * 10. Add authorization button if needed in AuthorizationButton.tsx
 * 11. Update FailedPostsNotification.tsx
 * 12. Update the additional channels switch in the relevant forms/*PostForm.tsx
 */

const EditorialDistributionQueue = () => {
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPostType, setNewPostType] =
    useState<EditorialFacebookPostType | null>(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);

  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') ?? '');

  const { pageId, status, network } = useParams();

  const debouncedSearch = useDebounce(search, 500);

  const { data: activePage, isLoading } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
      select: (data) => data.find((c) => c.id.toString() === pageId),
    },
  );

  const isNetworkSupported = useMemo(() => {
    switch (network) {
      case 'facebook':
      case 'text-status':
      case 'facebook-video':
      case 'reels':
        return !!activePage?.hasFacebook;
      case 'instagram':
        return !!activePage?.instagramAccountId;
      case 'threads':
        return !!activePage?.threadsAccountId;
      case 'pinterest':
        return !!activePage?.pinterestAccountId;
      case 'twitter':
        return !!activePage?.twitterAccountId;
      case 'whatsapp':
        return !!activePage?.whatsAppNewsletterId;
      case 'bluesky':
        return !!activePage?.hasBlueskyAccount;
      case 'youtube-shorts':
        return !!activePage?.hasYoutube;
      case 'linkedin':
        return !!activePage?.hasLinkedin;
      case 'tiktok':
        return !!activePage?.hasTiktok;
      case 'msn':
        return !!activePage?.hasMsn;
      case 'reddit':
        return !!activePage?.hasReddit;
      default:
        return false;
    }
  }, [activePage, network]);

  const renderPageInnerContent = () => {
    if (!isNetworkSupported) {
      return (
        <NotSupportedDescription>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
          <Text $type="heading2">
            {activePage?.name} page does not support selected network!
          </Text>
          <Text $type="caption">Switch to other network or page</Text>
        </NotSupportedDescription>
      );
    }

    switch (status) {
      case 'queued':
        return <Table search={debouncedSearch} activePage={activePage!} />;
      case 'posted':
      case 'deleted':
      case 'failed':
        return <PostsTable search={debouncedSearch} activePage={activePage!} />;
      case 'archive':
        return (
          <ArchiveTable search={debouncedSearch} activePage={activePage!} />
        );
      default:
        return null;
    }
  };

  const renderNewPostForm = () => {
    switch (newPostType) {
      case EditorialFacebookPostType.LINK:
        return (
          <LinkPostForm
            onClose={() => setNewPostType(null)}
            activePage={activePage!}
          />
        );
      case EditorialFacebookPostType.IMAGE:
        return (
          <ImagePostForm
            onClose={() => setNewPostType(null)}
            activePage={activePage!}
          />
        );
      case EditorialFacebookPostType.VIDEO:
      case EditorialFacebookPostType.REEL:
        return (
          <VideoPostForm
            onClose={() => setNewPostType(null)}
            activePage={activePage!}
            postType={newPostType}
          />
        );
      case EditorialFacebookPostType.STORY:
        return (
          <StoryPostForm
            onClose={() => setNewPostType(null)}
            activePage={activePage!}
          />
        );
      case EditorialFacebookPostType.TEXT:
        return (
          <TextPostForm
            onClose={() => setNewPostType(null)}
            activePage={activePage!}
          />
        );
      case EditorialFacebookPostType.IMAGE_ALBUM:
        return (
          <ImageAlbumPostForm
            onClose={() => setNewPostType(null)}
            activePage={activePage!}
          />
        );
      case EditorialFacebookPostType.PLACEHOLDER:
        return (
          <PlaceholderPostForm
            onClose={() => setNewPostType(null)}
            activePage={activePage!}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (activePage) {
      set('editorial_distribution_queue_active_page', {
        id: pageId,
        status,
        network,
      });
    }
  }, [pageId, status, network, activePage]);

  if (isLoading) {
    return <ContentSpinner />;
  }

  if (!activePage) {
    return <Navigate to="/general/editorial-distribution-queue" replace />;
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <HeadingsRow>
        <PageHeading>Distribution Queue</PageHeading>
        {isNetworkSupported && (
          <NewPostButton onClick={() => setShowNewPostModal(true)}>
            Add new post
          </NewPostButton>
        )}
      </HeadingsRow>
      <Tabs>
        <StyledNetworkSwitch />
        <Actions>
          {isNetworkSupported && <QueueSwitch activePage={activePage} />}
          <PagesSelection activePage={activePage} />
          {isNetworkSupported && (
            <StyledFilterButton
              text="Frequency"
              iconOnly
              isActive={false}
              onClick={() => setShowFrequencyModal(true)}
              filterApplied={false}
              icon={<FrequencyIcon />}
            />
          )}
          <StyledFullScreenButton />
        </Actions>
      </Tabs>
      <SearchBar
        placeholder="Search"
        setValue={setSearch}
        value={search}
        disabled={!isNetworkSupported}
      />
      <Notifications>
        <FailedPostsNotification activePage={activePage} />
        <AutoDeleteNotification activePage={activePage} />
        <PerformanceHoldNotification activePage={activePage} />
      </Notifications>
      {renderPageInnerContent()}
      <AnimatePresence>
        {showFrequencyModal && (
          <FrequencyModal
            onClose={() => setShowFrequencyModal(false)}
            activePage={activePage}
          />
        )}
        {showNewPostModal && (
          <NewPostModal
            onClose={() => setShowNewPostModal(false)}
            setNewPostType={setNewPostType}
          />
        )}
        {showPerformanceModal && (
          <PerformanceModal
            onClose={() => setShowPerformanceModal(false)}
            activePage={activePage}
          />
        )}
        {renderNewPostForm()}
      </AnimatePresence>
    </>
  );
};

export default EditorialDistributionQueue;
